// Rich, self-contained mock data for the Recommendations feature, gated behind
// the staff "Use mock data" toggle (see RecommendationsMockDataContext). It
// mirrors the reporting mock-data module: each export is typed to the exact
// generated query shape so it stays in sync with the schema, and query-backed
// components skip Apollo and read from here when the flag is on.
//
// Coverage is deliberately broad so the whole flow can be demoed end to end:
// several recommendations spread across every status (Inbox / Measuring impact
// / Done / Dismissed), each with real-sounding sample reviews, plus weekly
// impact trends shaped so the acted-on recommendations render a beautiful
// before/after "measuring impact" chart.

import { addDays, addWeeks, format, startOfWeek, subDays } from "date-fns";
import {
  ActionItemPriority,
  GuestFeedbackCorrectiveActionAudience,
  GuestFeedbackCorrectiveActionStatus,
  GuestFeedbackRecommendationActionType,
  LibraryItemContentType,
} from "@src/types.generated";
import type {
  GuestFeedbacksInput,
  PaginationInput,
} from "@src/types.generated";
import type { GuestFeedbackRecommendationsQuery } from "./GuestFeedbackView.generated";
import type { GuestFeedbackRecommendationDetailQuery } from "./RecommendationDetail.generated";
import type {
  RecommendationImpactLocationVolumeQuery,
  RecommendationImpactTrendQuery,
} from "./RecommendationImpactChart.generated";
import type { GuestFeedbackReviewsTab_GuestFeedbacksQuery } from "./GuestFeedbackReviewsTab.generated";
import type { GuestFeedbackThemeSummaryQuery } from "./TopThemesSidebar.generated";
import type { GuestFeedbackRatingTrendQuery } from "./GuestFeedbackRatingTrendChart.generated";

type MockRecommendationListItem =
  GuestFeedbackRecommendationsQuery["GuestFeedbackRecommendations"][number];
type MockRecommendationDetail =
  GuestFeedbackRecommendationDetailQuery["GuestFeedbackCorrectiveActions"][number];
type MockTrendPoint =
  RecommendationImpactTrendQuery["GuestFeedbackRatingTrend"][number];
type MockVolumePoint =
  RecommendationImpactLocationVolumeQuery["GuestFeedbackRatingTrend"][number];

// ---------------------------------------------------------------------------
// Date helpers — all mock dates are relative to "now" so the impact charts stay
// current whenever the demo is viewed (intervention a few weeks back, trend
// running up to this week).
// ---------------------------------------------------------------------------

const daysAgoISO = (days: number): string =>
  subDays(new Date(), days).toISOString();

const daysAgoDate = (days: number): string =>
  format(subDays(new Date(), days), "yyyy-MM-dd");

// Monday-anchored week start `weeks` before the current week, as YYYY-MM-DD to
// match the backend's date-only weekStart strings.
const weekStartString = (weeksAgo: number): string =>
  format(
    addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), -weeksAgo),
    "yyyy-MM-dd",
  );

// Small deterministic jitter from a string seed + index, so counts look organic
// but never shift between renders.
const jitter = (seed: string, index: number, spread: number): number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 100000;
  }
  return ((hash + index * 37) % (spread * 2 + 1)) - spread;
};

// ---------------------------------------------------------------------------
// Themes — each acted-on recommendation is tied to a theme, which is also the
// key the impact chart queries by.
// ---------------------------------------------------------------------------

type MockTheme = {
  id: string;
  name: string;
  description: string;
  feedbackCount: number;
};

const THEMES: Record<string, MockTheme> = {
  orderAccuracy: {
    id: "mock-theme-order-accuracy",
    name: "Order accuracy",
    description:
      "Guests receiving incorrect or incomplete orders, especially at the drive-thru during peak hours.",
    feedbackCount: 142,
  },
  greeting: {
    id: "mock-theme-greeting",
    name: "Guest greeting",
    description:
      "Guests reporting they weren't acknowledged or greeted when arriving.",
    feedbackCount: 63,
  },
  cleanliness: {
    id: "mock-theme-cleanliness",
    name: "Restroom cleanliness",
    description:
      "Complaints about restroom cleanliness and restocking during busy periods.",
    feedbackCount: 98,
  },
  foodTemp: {
    id: "mock-theme-food-temp",
    name: "Food temperature",
    description: "Fries and hot items arriving cold, particularly at dinner.",
    feedbackCount: 77,
  },
  serviceSpeed: {
    id: "mock-theme-service-speed",
    name: "Service speed",
    description: "Long wait times during the lunch rush.",
    feedbackCount: 121,
  },
  mobileOrder: {
    id: "mock-theme-mobile-order",
    name: "Mobile order pickup",
    description:
      "Confusion locating and collecting mobile / app orders in-store.",
    feedbackCount: 45,
  },
  upsell: {
    id: "mock-theme-upsell",
    name: "Upselling",
    description: "Missed suggestive-selling opportunities at the register.",
    feedbackCount: 38,
  },
};

// ---------------------------------------------------------------------------
// Recommendation specs — the single source of truth. Both the list-item and
// detail shapes are derived from these so a recommendation is identical wherever
// it appears.
// ---------------------------------------------------------------------------

type RecSpec = {
  id: string;
  title: string;
  issueDescription: string;
  remediation: string;
  priority: ActionItemPriority;
  status: GuestFeedbackCorrectiveActionStatus;
  audience: GuestFeedbackCorrectiveActionAudience;
  theme: MockTheme;
  affectedLocations: string[];
  locationIds: number[];
  // Days-ago offsets; converted to timestamps at read time.
  detectedDaysAgo: number;
  acceptedDaysAgo?: number;
  completedDaysAgo?: number;
  sampleReviews: string[];
};

const buildRecSpecs = (): RecSpec[] => [
  // ⭐ The hero "Measuring impact" recommendation — training assigned ~5 weeks
  // ago, mentions dropping sharply. Renders the green "improving" verdict with
  // populated Issue rate / Issues per week tiles and a clean before/after chart.
  {
    id: "mock-rec-order-accuracy",
    title: "Reduce incorrect drive-thru orders during peak hours",
    issueDescription:
      "Guests are consistently reporting missing items and wrong orders at the drive-thru, concentrated during the lunch and dinner rushes. The most common issues are missing sides, incorrect modifications, and wrong drinks. Several guests noted they didn't check their bag until they'd left, compounding the frustration.",
    remediation:
      "## Order accuracy at the drive-thru\n\nThis short course reinforces the order-confirmation habits that catch mistakes before the bag leaves the window.\n\n**What it covers**\n\n- Reading the full order back to the guest at the speaker\n- The two-point bag check: match the screen, then match the bag\n- Confirming modifications and special requests out loud\n- Handling the rush without skipping confirmation steps\n\n**Why it matters**\n\nAn incorrect order costs a remake, a refund, and often a guest. A five-second read-back is the cheapest quality check in the building.",
    priority: ActionItemPriority.High,
    status: GuestFeedbackCorrectiveActionStatus.MeasuringImpact,
    audience: GuestFeedbackCorrectiveActionAudience.Both,
    theme: THEMES.orderAccuracy,
    affectedLocations: ["Downtown", "Airport Rd", "Westgate Mall"],
    locationIds: [101, 102, 103],
    detectedDaysAgo: 44,
    acceptedDaysAgo: 35,
    sampleReviews: [
      "Ordered two combos at the drive-thru and got home to find both sides missing. Second time this month.",
      "Asked for no onions, got extra onions. The line was long so I get it's busy, but please double-check.",
      "Wrong drinks again. I ordered lemonades and got sweet tea. Kids were not happy.",
      "They forgot my sauce and one of the burgers. Had to drive back during my lunch break.",
    ],
  },
  // A second "Measuring impact" recommendation still in the collecting window —
  // assigned just days ago, so it shows the blue "Measuring impact." banner
  // (not enough post-training feedback yet to call a trend).
  {
    id: "mock-rec-greeting",
    title: "Greet every guest within 10 seconds of arrival",
    issueDescription:
      "A recurring theme in recent reviews is guests feeling ignored when they walk in — standing at the counter without acknowledgement, especially when staff are focused on mobile and drive-thru orders. First impressions are setting a negative tone for the whole visit.",
    remediation:
      "## The 10-second greeting\n\nA quick, genuine acknowledgement sets the tone for the entire visit.\n\n**What it covers**\n\n- Making eye contact and greeting within 10 seconds, even when hands are full\n- The 'I'll be right with you' hold when mid-task\n- Owning the front counter during mobile/drive-thru surges\n\nThis course covers greeting guests naturally under real conditions: hands full, mid-task, or a swamped front counter.\n\n**Why it matters**\n\nGuests decide how a visit is going to go in the first ten seconds of the visit. A quick 'I'll be right with you' takes no extra time and changes how the whole visit feels.",
    priority: ActionItemPriority.Medium,
    status: GuestFeedbackCorrectiveActionStatus.MeasuringImpact,
    audience: GuestFeedbackCorrectiveActionAudience.Both,
    theme: THEMES.greeting,
    affectedLocations: ["Downtown", "Lakeside"],
    locationIds: [101, 104],
    detectedDaysAgo: 12,
    acceptedDaysAgo: 4,
    sampleReviews: [
      "Stood at the counter for a full two minutes while three employees looked right past me. Finally left.",
      "No hello, no eye contact, nothing. Felt like I was interrupting them by wanting to order.",
      "The team was clearly slammed with app orders but a quick 'be right with you' would have gone a long way.",
    ],
  },
  // ✅ Done — completed a week ago after ~11 weeks of measurement, showing a
  // sustained improvement (green verdict, low post rate).
  {
    id: "mock-rec-cleanliness",
    title: "Improve restroom cleanliness during peak periods",
    issueDescription:
      "Guests repeatedly flagged dirty and under-stocked restrooms, particularly on weekend afternoons. Comments referenced overflowing trash, empty soap dispensers, and wet floors — a food-safety perception risk as much as a comfort one.",
    remediation:
      "## Restroom readiness\n\nA clean restroom is one of the strongest signals of overall cleanliness a guest experiences.\n\n**What it covers**\n\n- The 30-minute restroom check cadence during peak periods\n- The restock checklist: soap, towels, tissue, trash\n- Logging checks so nothing is missed on a busy shift\n\n**Why it matters**\n\nGuests judge a restaurant's cleanliness by its restroom, often more than anything in the dining room. A 30-minute check during peak hours keeps that impression positive.",
    priority: ActionItemPriority.High,
    status: GuestFeedbackCorrectiveActionStatus.Completed,
    audience: GuestFeedbackCorrectiveActionAudience.CurrentStaff,
    theme: THEMES.cleanliness,
    affectedLocations: ["Westgate Mall", "Airport Rd"],
    locationIds: [103, 102],
    detectedDaysAgo: 88,
    acceptedDaysAgo: 77,
    completedDaysAgo: 7,
    sampleReviews: [
      "Restroom was a mess on Saturday afternoon — no soap and the trash was overflowing.",
      "Floors were wet with no sign out. Food was fine but the bathroom made me second-guess the kitchen.",
      "Please check the restrooms more often on weekends. Consistently the worst part of an otherwise good spot.",
    ],
  },
  // ✅ Done — a second completed recommendation for board variety.
  {
    id: "mock-rec-food-temp",
    title: "Serve fries and hot items at temperature at dinner",
    issueDescription:
      "Dinner-service reviews frequently mention cold fries and lukewarm entrees. The pattern points to items being bagged ahead of the rest of the order and sitting under the heat lamp too long.",
    remediation:
      "## Hot food, hot\n\n**What it covers**\n\n- Timing fries to the rest of the order, not ahead of it\n- Heat-lamp hold-time limits and when to re-drop\n- The final temperature check before the bag is sealed\n\n**Why it matters**\n\nCold fries are one of the fastest ways a guest decides the whole meal was a letdown, even when everything else in the order was right. Fixing it is just a matter of timing.",
    priority: ActionItemPriority.Medium,
    status: GuestFeedbackCorrectiveActionStatus.Completed,
    audience: GuestFeedbackCorrectiveActionAudience.Both,
    theme: THEMES.foodTemp,
    affectedLocations: ["Lakeside"],
    locationIds: [104],
    detectedDaysAgo: 70,
    acceptedDaysAgo: 56,
    completedDaysAgo: 3,
    sampleReviews: [
      "Fries were cold and limp by the time I got to the table — and I ate in the restaurant.",
      "Burger was warm at best. Feels like everything sits too long before it's handed over.",
    ],
  },
  // 📥 Inbox — pending, high priority. New "create a course" recommendation.
  {
    id: "mock-rec-service-speed",
    title: "Improve lunch rush wait times",
    issueDescription:
      "Wait times between 11:30 and 1:00 are drawing repeated complaints, with several guests reporting 15+ minute waits and abandoned orders. The bottleneck appears to be at assembly, not the register.",
    remediation:
      "## Beating the lunch rush\n\n**What it covers**\n\n- Positioning and hand-offs during peak volume\n- Pre-rush prep that removes assembly bottlenecks\n- Communicating realistic wait times to guests in line\n\n**Why it matters**\n\nAbandoned lunch orders often turn into complaints, and sometimes into bad reviews. Ten minutes of pre-rush prep takes far less time than recovering from a poor rating.",
    priority: ActionItemPriority.High,
    status: GuestFeedbackCorrectiveActionStatus.Pending,
    audience: GuestFeedbackCorrectiveActionAudience.Both,
    theme: THEMES.serviceSpeed,
    affectedLocations: ["Downtown", "Airport Rd", "Westgate Mall", "Lakeside"],
    locationIds: [101, 102, 103, 104],
    detectedDaysAgo: 3,
    sampleReviews: [
      "Waited 18 minutes for two sandwiches at lunch. Ended up late getting back to work.",
      "The line was out the door and barely moving. Great food but I can't do that wait again.",
      "Ordered on the app to save time and still waited 15 minutes past the pickup time.",
    ],
  },
  // 🚧 In progress — action taken, not yet measuring impact.
  {
    id: "mock-rec-mobile-order",
    title: "Clarify mobile order pickup in-store",
    issueDescription:
      "App users are confused about where to collect mobile orders — there's no clear pickup shelf or signage, so guests queue in the main line and lose the time the app promised them.",
    remediation:
      "## Mobile pickup that just works\n\n**What it covers**\n\n- Directing app guests to the pickup area\n- Keeping the mobile shelf organized and labeled\n- Closing the loop when an order isn't ready on time\n\n**Why it matters**\n\nA guest who orders ahead to skip the line and ends up in it anyway won't use the app again. A labeled shelf and a clear handoff keeps our brand promise.",
    priority: ActionItemPriority.Medium,
    status: GuestFeedbackCorrectiveActionStatus.InProgress,
    audience: GuestFeedbackCorrectiveActionAudience.Both,
    theme: THEMES.mobileOrder,
    affectedLocations: ["Downtown", "Westgate Mall"],
    locationIds: [101, 103],
    detectedDaysAgo: 5,
    acceptedDaysAgo: 2,
    sampleReviews: [
      "No idea where to pick up my app order. Waited in the regular line for 10 minutes for nothing.",
      "There's no mobile pickup shelf. I had to flag someone down and they seemed annoyed I asked.",
    ],
  },
  // 📥 Inbox — pending, low priority.
  {
    id: "mock-rec-upsell",
    title: "Reintroduce suggestive selling at the register",
    issueDescription:
      "Not a complaint theme but an opportunity: mystery-shop notes and guest comments suggest add-ons and combos are rarely offered, leaving check size and guest experience on the table.",
    remediation:
      "## Suggestive selling that helps the guest\n\n**What it covers**\n\n- Reading the order and offering a genuine, relevant add-on\n- Combo and size-up prompts that feel helpful, not pushy\n- Making it a natural close to every order\n\n**Why it matters**\n\nA guest who is asked about a combo or a size-up often says yes without feeling sold to, and that adds real dollars to the average check.",
    priority: ActionItemPriority.Low,
    status: GuestFeedbackCorrectiveActionStatus.Pending,
    audience: GuestFeedbackCorrectiveActionAudience.NewHire,
    theme: THEMES.upsell,
    affectedLocations: ["Airport Rd"],
    locationIds: [102],
    detectedDaysAgo: 8,
    sampleReviews: [
      "Would've happily added a shake if anyone had mentioned it. Never got asked.",
    ],
  },
  // 🚫 Dismissed — kept off the board but available on the Dismissed view.
  {
    id: "mock-rec-music",
    title: "Lower background music volume",
    issueDescription:
      "A handful of reviews mention the music being too loud to hold a conversation. Low signal and largely location-specific, so this was dismissed in favor of higher-impact issues.",
    remediation:
      "## Dining-room ambience\n\n**What it covers**\n\n- Setting music to the daypart-appropriate volume\n- Checking the room from a guest's seat, not behind the counter\n\n**Why it matters**\n\nA comfortable dining room is part of what keeps guests coming back.",
    priority: ActionItemPriority.Low,
    status: GuestFeedbackCorrectiveActionStatus.Rejected,
    audience: GuestFeedbackCorrectiveActionAudience.CurrentStaff,
    theme: THEMES.mobileOrder,
    affectedLocations: ["Lakeside"],
    locationIds: [104],
    detectedDaysAgo: 21,
    sampleReviews: [
      "Music was so loud we couldn't talk across the table. Had to ask them to turn it down.",
    ],
  },
];

const toSampleReviews = (
  spec: RecSpec,
): MockRecommendationListItem["sampleReviews"] =>
  spec.sampleReviews.map((review, index) => ({
    __typename: "GuestFeedbackSampleReview" as const,
    id: `${spec.id}-review-${index}`,
    review,
  }));

const toListItem = (spec: RecSpec): MockRecommendationListItem => ({
  __typename: "GuestFeedbackCorrectiveAction",
  id: spec.id,
  title: spec.title,
  issueDescription: spec.issueDescription,
  priority: spec.priority,
  status: spec.status,
  actionType: GuestFeedbackRecommendationActionType.Create,
  affectedLocations: spec.affectedLocations,
  acceptedAt:
    spec.acceptedDaysAgo != null ? daysAgoISO(spec.acceptedDaysAgo) : null,
  completedAt:
    spec.completedDaysAgo != null ? daysAgoISO(spec.completedDaysAgo) : null,
  measuringImpactStartedOn:
    (spec.status === GuestFeedbackCorrectiveActionStatus.MeasuringImpact ||
      spec.status === GuestFeedbackCorrectiveActionStatus.Completed) &&
    spec.acceptedDaysAgo != null
      ? daysAgoDate(spec.acceptedDaysAgo)
      : null,
  audience: spec.audience,
  analysisRun: {
    __typename: "GuestFeedbackAnalysisRun",
    id: `${spec.id}-run`,
    startedAt: daysAgoISO(spec.detectedDaysAgo),
    completedAt: daysAgoISO(spec.detectedDaysAgo),
  },
  theme: {
    __typename: "GuestFeedbackTheme",
    id: spec.theme.id,
    name: spec.theme.name,
  },
  locationGroup: null,
  locations: spec.locationIds.map((id, index) => ({
    __typename: "Location" as const,
    id,
    name: spec.affectedLocations[index] ?? "",
  })),
  sampleReviews: toSampleReviews(spec),
});

const toDetail = (spec: RecSpec): MockRecommendationDetail => ({
  __typename: "GuestFeedbackCorrectiveAction",
  id: spec.id,
  title: spec.title,
  issueDescription: spec.issueDescription,
  remediation: spec.remediation,
  priority: spec.priority,
  status: spec.status,
  actionType: GuestFeedbackRecommendationActionType.Create,
  acceptedAt:
    spec.acceptedDaysAgo != null ? daysAgoISO(spec.acceptedDaysAgo) : null,
  completedAt:
    spec.completedDaysAgo != null ? daysAgoISO(spec.completedDaysAgo) : null,
  measuringImpactStartedOn:
    (spec.status === GuestFeedbackCorrectiveActionStatus.MeasuringImpact ||
      spec.status === GuestFeedbackCorrectiveActionStatus.Completed) &&
    spec.acceptedDaysAgo != null
      ? daysAgoDate(spec.acceptedDaysAgo)
      : null,
  affectedLocations: spec.affectedLocations,
  // Course, so "Generate course" opens the real course-creation flow (the
  // generated course is real; only the recommendation itself is mock).
  contentType: LibraryItemContentType.Course,
  theme: {
    __typename: "GuestFeedbackTheme",
    id: spec.theme.id,
    name: spec.theme.name,
    description: spec.theme.description,
    feedbackCount: spec.theme.feedbackCount,
  },
  libraryItem: null,
  locationGroup: null,
  locations: spec.locationIds.map((id) => ({
    __typename: "Location" as const,
    id,
  })),
  targetRoles: [],
  sampleReviews: toSampleReviews(spec),
});

export const getMockRecommendations = (): MockRecommendationListItem[] =>
  buildRecSpecs().map(toListItem);

export const getMockRecommendationDetail = (
  id: string,
): MockRecommendationDetail | null => {
  const spec = buildRecSpecs().find((s) => s.id === id);
  return spec ? toDetail(spec) : null;
};

// ---------------------------------------------------------------------------
// Impact trends — weekly theme-mention series (numerator) and all-review volume
// (denominator), keyed by theme. Shaped so the acted-on recommendations render
// a clear, attractive before/after: a steady baseline, a sharp drop after the
// intervention week, and a rising average rating.
// ---------------------------------------------------------------------------

// Weeks of history to synthesize (before the current week through this week).
const TREND_WEEKS = 14;

type ThemeTrendConfig = {
  interventionWeeksAgo: number;
  baselinePerWeek: number;
  postPerWeek: number;
  baselineRating: number;
  postRating: number;
};

// Keyed by theme id. Only the acted-on recommendations (Measuring impact / Done)
// ever surface the impact chart, so only their themes need a config; anything
// else falls back to a generic improving shape.
const THEME_TREND: Record<string, ThemeTrendConfig> = {
  [THEMES.orderAccuracy.id]: {
    interventionWeeksAgo: 5,
    baselinePerWeek: 11,
    postPerWeek: 3,
    baselineRating: 3.2,
    postRating: 4.4,
  },
  [THEMES.greeting.id]: {
    // Assigned this week — not enough post data yet, so the chart stays in the
    // blue "Measuring impact." collecting state.
    interventionWeeksAgo: 0,
    baselinePerWeek: 6,
    postPerWeek: 6,
    baselineRating: 3.4,
    postRating: 3.5,
  },
  [THEMES.cleanliness.id]: {
    interventionWeeksAgo: 11,
    baselinePerWeek: 9,
    postPerWeek: 2,
    baselineRating: 3.1,
    postRating: 4.5,
  },
  [THEMES.foodTemp.id]: {
    interventionWeeksAgo: 8,
    baselinePerWeek: 7,
    postPerWeek: 2,
    baselineRating: 3.3,
    postRating: 4.3,
  },
};

const DEFAULT_TREND: ThemeTrendConfig = {
  interventionWeeksAgo: 5,
  baselinePerWeek: 9,
  postPerWeek: 3,
  baselineRating: 3.3,
  postRating: 4.3,
};

export const getMockImpactTrend = (themeId: string): MockTrendPoint[] => {
  const cfg = THEME_TREND[themeId] ?? DEFAULT_TREND;
  const points: MockTrendPoint[] = [];
  // Iterate oldest → newest (weeksAgo TREND_WEEKS-1 down to 0).
  for (let i = TREND_WEEKS - 1; i >= 0; i -= 1) {
    // Weeks elapsed since the intervention: negative before, 0 at, positive after.
    const sinceIntervention = cfg.interventionWeeksAgo - i;
    let count: number;
    let rating: number;
    if (sinceIntervention < 0) {
      count = cfg.baselinePerWeek + jitter(themeId, i, 2);
      rating = cfg.baselineRating;
    } else {
      // Ease from baseline down to post over ~4 weeks after the intervention.
      const progress = Math.min(sinceIntervention / 4, 1);
      count = Math.round(
        cfg.baselinePerWeek +
          (cfg.postPerWeek - cfg.baselinePerWeek) * progress +
          jitter(themeId, i, 1),
      );
      rating =
        cfg.baselineRating + (cfg.postRating - cfg.baselineRating) * progress;
    }
    points.push({
      __typename: "GuestFeedbackRatingTrendPoint",
      weekStart: weekStartString(i),
      avgRating: Math.round(rating * 10) / 10,
      count: Math.max(0, count),
    });
  }
  return points;
};

export const getMockImpactVolume = (themeId: string): MockVolumePoint[] => {
  const points: MockVolumePoint[] = [];
  for (let i = TREND_WEEKS - 1; i >= 0; i -= 1) {
    points.push({
      __typename: "GuestFeedbackRatingTrendPoint",
      weekStart: weekStartString(i),
      // ~120–140 total reviews/week at the affected locations, so theme mentions
      // read as a small, clearly-shrinking share of overall feedback.
      count: 128 + jitter(themeId, i, 8),
    });
  }
  return points;
};

// ---------------------------------------------------------------------------
// Guest Feedback page mocks — the recommendations name "Guest Feedback" as
// their source, so when mock data is on the Guest Feedback page tells the same
// story: a reviews table, top-theme pills, and a rating trend, all derived from
// one synthesized review set so the counts agree wherever they appear.
// ---------------------------------------------------------------------------

type MockReviewRow =
  GuestFeedbackReviewsTab_GuestFeedbacksQuery["GuestFeedbacks"]["objects"][number];
type MockThemeSummaryItem =
  GuestFeedbackThemeSummaryQuery["GuestFeedbackThemeSummary"][number];
type MockRatingTrendPoint =
  GuestFeedbackRatingTrendQuery["GuestFeedbackRatingTrend"][number];

const MOCK_LOCATIONS = [
  { id: 101, name: "Downtown" },
  { id: 102, name: "Airport Rd" },
  { id: 103, name: "Westgate Mall" },
  { id: 104, name: "Lakeside" },
] as const;

// Negative review texts per theme; each theme's recommendation sampleReviews
// are folded in so a review a user reads on a recommendation also shows up in
// the source table.
const EXTRA_THEME_REVIEW_TEXTS: Record<string, string[]> = {
  [THEMES.orderAccuracy.id]: [
    "Checked the bag at the window this time and sure enough, a burger was missing.",
    "Two out of three orders this month had something wrong. Please slow down and read the screen.",
  ],
  [THEMES.greeting.id]: [
    "Walked in, stood there, nothing. A simple hello would fix half of this.",
  ],
  [THEMES.cleanliness.id]: [
    "Restroom needed attention badly during the Saturday rush.",
    "No paper towels and the floor was sticky. Not what I expect from this place.",
  ],
  [THEMES.foodTemp.id]: [
    "Fries were lukewarm at best. Great flavor when they're fresh, so it's a shame.",
  ],
  [THEMES.serviceSpeed.id]: [
    "20 minutes at lunch is just too long. The staff hustled but the line barely moved.",
    "Love the food but I've started going elsewhere on weekdays because of the wait.",
  ],
  [THEMES.mobileOrder.id]: [
    "App said ready at 12:10, nobody could find my order until 12:25.",
  ],
  [THEMES.upsell.id]: [],
};

// Positive filler so the table reads like a real feed and the average rating
// has room to climb after the interventions land.
const POSITIVE_REVIEW_TEXTS = [
  "Fast, friendly, and the order was exactly right. Best location around.",
  "The new crew at the counter is fantastic — greeted us right away.",
  "Food came out hot and fresh. Fries were perfect.",
  "Clean dining room, clean restrooms, quick service. No complaints.",
  "Mobile pickup was seamless — order was labeled and waiting on the shelf.",
  "Staff double-checked our order at the window and caught a missing item. Impressed.",
  "Great lunch stop. In and out in under ten minutes even with a line.",
  "Everything about this visit was on point. We'll be back.",
  "Cashier suggested the combo and it was actually a better deal. Nice touch.",
  "Consistently good — third visit this month and every order has been right.",
];

// Weekly negative-mention counts per theme, keyed off each theme's story:
// acted-on themes (order accuracy, cleanliness, food temp) fade after their
// intervention week; open ones (service speed, mobile, greeting) persist.
const themeWeeklyCount = (themeId: string, weeksAgo: number): number => {
  const cfg = THEME_TREND[themeId];
  if (cfg) {
    // Scale the impact-trend shape down to table-sized volume so complaints
    // stay a minority of the feed while still tracing the same before/after.
    const sinceIntervention = cfg.interventionWeeksAgo - weeksAgo;
    const perWeek =
      sinceIntervention < 0
        ? cfg.baselinePerWeek
        : cfg.baselinePerWeek +
          (cfg.postPerWeek - cfg.baselinePerWeek) *
            Math.min(sinceIntervention / 4, 1);
    return Math.max(0, Math.round(perWeek / 5));
  }
  // Open inbox themes: a steady trickle.
  if (themeId === THEMES.serviceSpeed.id) return 2;
  if (themeId === THEMES.mobileOrder.id) return 1;
  if (themeId === THEMES.upsell.id) return weeksAgo % 3 === 0 ? 1 : 0;
  return weeksAgo % 4 === 0 ? 1 : 0;
};

type MockReview = {
  row: MockReviewRow;
  themeId: string | null;
  date: Date;
  rating: number;
};

const buildMockReviews = (): MockReview[] => {
  const reviews: MockReview[] = [];
  const themeTexts: Record<string, string[]> = {};
  for (const spec of buildRecSpecs()) {
    themeTexts[spec.theme.id] = [
      ...(themeTexts[spec.theme.id] ?? []),
      ...spec.sampleReviews,
    ];
  }
  for (const [themeId, extras] of Object.entries(EXTRA_THEME_REVIEW_TEXTS)) {
    themeTexts[themeId] = [...(themeTexts[themeId] ?? []), ...extras];
  }

  let serial = 0;
  const push = (
    themeId: string | null,
    text: string,
    rating: number,
    date: Date,
  ) => {
    const location = MOCK_LOCATIONS[serial % MOCK_LOCATIONS.length];
    const id = `mock-review-${serial}`;
    serial += 1;
    reviews.push({
      themeId,
      date,
      rating,
      row: {
        __typename: "GuestFeedback",
        id,
        text,
        date: date.toISOString(),
        rating,
        feedbackSource: "Google Business",
        integrationLocation: {
          __typename: "GuestFeedbackIntegrationLocation",
          id: `${id}-integration-location`,
          sourceName: location.name,
          sourceLocationId: `mock-source-${location.id}`,
          location: {
            __typename: "Location",
            id: location.id,
            name: location.name,
          },
        },
        csvUploadLocation: null,
      },
    });
  };

  // Anchor each review inside its Monday-based week so the weekly rating trend
  // buckets exactly match the composition we generate (no spill across week
  // boundaries); the current partial week clamps to "now".
  const now = new Date();
  const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
  const dateInWeek = (weeksAgo: number, dayOffset: number): Date => {
    const date = addDays(addWeeks(currentWeekStart, -weeksAgo), dayOffset % 7);
    return date > now ? now : date;
  };

  for (let weeksAgo = TREND_WEEKS - 1; weeksAgo >= 0; weeksAgo -= 1) {
    let negatives = 0;
    for (const theme of Object.values(THEMES)) {
      const count = themeWeeklyCount(theme.id, weeksAgo);
      const texts = themeTexts[theme.id] ?? [];
      for (let i = 0; i < count && texts.length > 0; i += 1) {
        negatives += 1;
        const text = texts[(weeksAgo + i) % texts.length];
        // Upsell mentions are opportunities, not complaints; everything else
        // lands as a 1-3 star review (mostly 2-3, the occasional 1).
        const rating =
          theme.id === THEMES.upsell.id
            ? 4
            : Math.max(1, 3 - Math.abs(jitter(theme.id, weeksAgo * 7 + i, 2)));
        push(theme.id, text, rating, dateInWeek(weeksAgo, weeksAgo + i * 2));
      }
    }
    // Fill each week to ~14 reviews with positives so the star-rating trend
    // climbs as the negative themes fade.
    const positives = Math.max(0, 14 - negatives);
    for (let i = 0; i < positives; i += 1) {
      push(
        null,
        POSITIVE_REVIEW_TEXTS[
          (weeksAgo * 3 + i) % POSITIVE_REVIEW_TEXTS.length
        ],
        4 + ((weeksAgo + i) % 2),
        dateInWeek(weeksAgo, i * 3 + weeksAgo),
      );
    }
  }
  return reviews;
};

const filterMockReviews = (
  reviews: MockReview[],
  input: GuestFeedbacksInput | undefined,
): MockReview[] =>
  reviews.filter((review) => {
    if (input?.minRating != null && review.rating < input.minRating)
      return false;
    if (input?.maxRating != null && review.rating > input.maxRating)
      return false;
    if (input?.startDate && review.date < new Date(input.startDate))
      return false;
    if (input?.endDate && review.date > new Date(input.endDate)) return false;
    if (
      input?.search?.value &&
      !(review.row.text ?? "")
        .toLowerCase()
        .includes(input.search.value.toLowerCase())
    )
      return false;
    if (
      input?.themeIds?.length &&
      (!review.themeId || !input.themeIds.includes(review.themeId))
    )
      return false;
    if (
      input?.locationIds?.length &&
      !input.locationIds.includes(mockLocationId(review))
    )
      return false;
    return true;
  });

// Mirrors the backend's location filter: feedback reaches a location through
// either the integration mapping or the CSV upload mapping.
const mockLocationId = (review: MockReview): number =>
  review.row.integrationLocation?.location?.id ??
  review.row.csvUploadLocation?.location?.id ??
  -1;

export const getMockLocationOptions = (): { id: number; name: string }[] =>
  MOCK_LOCATIONS.map((location) => ({ id: location.id, name: location.name }));

export const getMockGuestFeedbacksData = (
  input: GuestFeedbacksInput | undefined,
  pagination: PaginationInput | null | undefined,
): GuestFeedbackReviewsTab_GuestFeedbacksQuery => {
  const filtered = filterMockReviews(buildMockReviews(), input);
  const descending = input?.sort?.descending ?? true;
  const byRating = input?.sort?.column === "rating";
  filtered.sort((a, b) => {
    const diff = byRating
      ? a.rating - b.rating
      : a.date.getTime() - b.date.getTime();
    return descending ? -diff : diff;
  });
  const offset = pagination?.offset ?? 0;
  const limit = pagination?.limit;
  const page =
    limit === 0
      ? []
      : limit != null
        ? filtered.slice(offset, offset + limit)
        : filtered.slice(offset);
  return {
    __typename: "Query",
    GuestFeedbacks: {
      __typename: "GuestFeedbacks",
      objects: page.map((review) => review.row),
      totalCount: filtered.length,
    },
  };
};

export const getMockThemeSummary = (input: {
  limit?: number | null;
  search?: string;
  startDate?: string;
  endDate?: string;
  minRating?: number;
  maxRating?: number;
  locationIds?: number[];
}): MockThemeSummaryItem[] => {
  const filtered = filterMockReviews(buildMockReviews(), {
    search: input.search ? { value: input.search } : undefined,
    startDate: input.startDate,
    endDate: input.endDate,
    minRating: input.minRating,
    maxRating: input.maxRating,
    locationIds: input.locationIds,
  });
  const counts = new Map<string, number>();
  for (const review of filtered) {
    if (review.themeId)
      counts.set(review.themeId, (counts.get(review.themeId) ?? 0) + 1);
  }
  return Object.values(THEMES)
    .filter((theme) => (counts.get(theme.id) ?? 0) > 0)
    .map((theme) => ({
      __typename: "GuestFeedbackTheme" as const,
      id: theme.id,
      name: theme.name,
      mentionCount: counts.get(theme.id) ?? 0,
    }))
    .sort((a, b) => (b.mentionCount ?? 0) - (a.mentionCount ?? 0))
    .slice(0, input.limit ?? undefined);
};

// Star-rating trend (no theme) or per-theme mention trend for the Guest
// Feedback page chart, computed from the same synthesized reviews as the table.
export const getMockGuestFeedbackRatingTrend = (input: {
  startDate?: string;
  themeId?: string | null;
  locationIds?: number[];
}): MockRatingTrendPoint[] => {
  const reviews = buildMockReviews().filter((review) => {
    if (input.themeId && review.themeId !== input.themeId) return false;
    if (input.startDate && review.date < new Date(input.startDate))
      return false;
    if (
      input.locationIds?.length &&
      !input.locationIds.includes(mockLocationId(review))
    )
      return false;
    return true;
  });
  const byWeek = new Map<string, { total: number; count: number }>();
  for (const review of reviews) {
    const weekStart = format(
      startOfWeek(review.date, { weekStartsOn: 1 }),
      "yyyy-MM-dd",
    );
    const bucket = byWeek.get(weekStart) ?? { total: 0, count: 0 };
    bucket.total += review.rating;
    bucket.count += 1;
    byWeek.set(weekStart, bucket);
  }
  return [...byWeek.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, bucket]) => ({
      __typename: "GuestFeedbackRatingTrendPoint" as const,
      weekStart,
      avgRating: Math.round((bucket.total / bucket.count) * 10) / 10,
      count: bucket.count,
    }));
};
